import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as signalR from "@microsoft/signalr";
import {
  setNotifications,
  appendNotification,
  markAsRead,
  setLoading,
  setError,
} from "./notificationsSlice";
import * as notificationsAPI from "./notificationsAPI";
import { useApp } from "../../contexts/AppContext";
import { FormattedMessage, useIntl } from "react-intl";
import { BellRing } from "lucide-react";
const Notifications = ({ technicianId, isOpen, onClose, onNavigate }) => {
  const dispatch = useDispatch();
  const intl = useIntl;
  const notificationsRaw = useSelector((state) => state.notifications.items);
  const notifications = Array.isArray(notificationsRaw) ? notificationsRaw : [];
  const loading = useSelector((state) => state.notifications.loading);
  const error = useSelector((state) => state.notifications.error);
  const { user } = useApp();
  const userId = user?.employeeid;
  const loadNotifications = useCallback(
    async (id) => {
      dispatch(setLoading(true));
      try {
        dispatch(notificationsAPI.fetchNotifications(id));
      } catch (err) {
        dispatch(setError(err.message || "Failed to load notifications"));
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  const handleMarkRead = async (id) => {
    try {
      dispatch(notificationsAPI.markNotificationAsRead(id));
      dispatch(markAsRead(id));
    } catch (err) {
      dispatch(setError(err.message || "Failed to mark as read"));
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.jobId) {
      alert("This notification does not have a related job/appointment!");
      return;
    }
    handleMarkRead(notification.id);
    onNavigate(notification.jobId, notification.jobId);
    onClose();

    //setNotificationsOpen(false);
  };

  useEffect(() => {
    if (!userId) {
      console.error("No technician ID provided");
      return;
    }
    loadNotifications(userId);

    const signalRUrl = `${process.env.REACT_APP_HOST_URL}notificationHub?technicianId=${userId}`;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(signalRUrl)
      .withAutomaticReconnect()
      .build();

    connection.on("ReceiveNotification", (notification) => {
      dispatch(appendNotification(notification));
    });

    connection
      .start()
      .then(() => console.log("SignalR connected"))
      .catch((err) => console.error("SignalR error", err));

    return () => {
      connection.stop();
    };
  }, [technicianId]);

  if (!isOpen) return null;
  if (loading)
    return (
      <div className="p-4">
        {" "}
        <FormattedMessage
          id="Notification.Loading"
          defaultMessage="Loading notifications..."
        />
      </div>
    );
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;

  return (
    <div className="fixed top-0 right-0 h-full w-80 bg-white dark:bg-gray-900 shadow-lg z-40 transition-transform duration-300">
      <div className="p-4 border-b border-gray-300 dark:border-gray-700 flex justify-between items-center">
        <h3 className="text-lg font-semibold">
          <FormattedMessage id="Notifications" defaultMessage="Notifications" />
        </h3>
        <button
          onClick={onClose}
          aria-label="Close notifications drawer"
          className="p-1"
        >
          ✕
        </button>
      </div>
      <div className="overflow-y-auto p-3 h-[calc(100%-56px)]">
        {notifications.length === 0 && (
          <p className="text-center text-gray-500">
            <FormattedMessage
              id="NoNotifications"
              defaultMessage="No notifications available"
            />
          </p>
        )}
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className="flex items-center gap-3 p-2 mb-2 cursor-pointer rounded-md border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700"
            onClick={() => handleNotificationClick(notification)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleNotificationClick(notification);
              }
            }}
            aria-label={`Notification: ${notification.title}`}
          >
            {/* Circle with icon */}
            <div
              className={`flex items-center justify-center h-8 w-8 rounded-full ${
                notification.isRead
                  ? "bg-gray-200 dark:bg-gray-700"
                  : "bg-blue-100 dark:bg-blue-700"
              }`}
            >
              <BellRing
                className={`h-4 w-4 ${
                  notification.isRead
                    ? "text-gray-500 dark:text-gray-300"
                    : "text-blue-600 dark:text-blue-300"
                }`}
              />
            </div>

            {/* Content */}
            <div className="flex-1">
              <div
                className={`${
                  notification.isRead
                    ? "text-gray-800 dark:text-gray-200"
                    : "font-semibold text-blue-700 dark:text-blue-300"
                }`}
              >
                {notification.title}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(notification.timestamp).toLocaleString([], {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Notifications;
